import { Controller } from "@hotwired/stimulus"

const TRIGGER_KEYS = new Set(["Enter", " ", "Spacebar", "ArrowDown"])
const ACTIVATION_KEYS = new Set(["Enter", " ", "Spacebar"])

export default class extends Controller {
  static targets = [
    "trigger",
    "summary",
    "icon",
    "menu",
    "search",
    "searchClear",
    "menuBody",
    "row",
    "checkbox",
    "create",
    "createEditor",
    "createInput",
    "hiddenInput",
    "filterSelection",
    "filterCount",
    "filterCountWrapper",
    "selectedStack",
    "selectedList",
    "selectedListItemTemplate",
    "selectedTotalCount",
  ]

  static values = {
    mode: { type: String, default: "single" },
    placeholder: { type: String, default: "Select option" },
    searchable: { type: Boolean, default: false },
    creatable: { type: Boolean, default: false },
    hierarchical: { type: Boolean, default: false },
    createLabel: { type: String, default: "New list" },
    submitOnChange: { type: Boolean, default: false },
    emitHiddenChange: { type: Boolean, default: false },
    panelSelector: String,
  }

  connect() {
    this.handleDocumentClickBound = this.handleDocumentClick.bind(this)
    this.handleDocumentKeydownBound = this.handleDocumentKeydown.bind(this)
    this.removeFilterSelectionBound = this.removeFilterSelection.bind(this)
    this.removeSelectedValueBound = this.removeSelectedValue.bind(this)

    document.addEventListener("click", this.handleDocumentClickBound)
    document.addEventListener("keydown", this.handleDocumentKeydownBound)

    this.normalizeRows()
    this.syncAll()
    this.syncOptionAria()
    this.hide()

    if (this.hasFilterSelectionTarget) {
      this.filterSelectionTarget.addEventListener("click", this.removeFilterSelectionBound)

      if (typeof ResizeObserver !== "undefined") {
        this.tokenResizeObserver = new ResizeObserver(() => this.syncTokenOverflow())
        this.tokenResizeObserver.observe(this.filterSelectionTarget)
      }
    }

    if (this.hasSelectedListTarget) {
      this.selectedListTarget.addEventListener("click", this.removeSelectedValueBound)
    }
  }

  disconnect() {
    document.removeEventListener("click", this.handleDocumentClickBound)
    document.removeEventListener("keydown", this.handleDocumentKeydownBound)

    if (this.hasFilterSelectionTarget) {
      this.filterSelectionTarget.removeEventListener("click", this.removeFilterSelectionBound)
    }

    this.tokenResizeObserver?.disconnect()

    if (this.hasSelectedListTarget) {
      this.selectedListTarget.removeEventListener("click", this.removeSelectedValueBound)
    }
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    this.isOpen() ? this.hide() : this.show()
  }

  show() {
    this.hideOtherOpenMenus()

    this.menuTarget.classList.remove("hidden")
    this.triggerTarget.setAttribute("aria-expanded", "true")
    this.menuTarget.setAttribute("aria-hidden", "false")
    const activeRow = this.selectedOrFirstVisibleRow()
    this.syncActiveDescendant(activeRow)

    if (this.hasIconTarget) {
      this.iconTarget.classList.add("is-open")
    }

    if (this.hasSearchTarget) {
      this.syncSearchClearVisibility()
      this.searchTarget.focus({ preventScroll: true })
      this.searchTarget.select()
    }

    this.scrollSelectedOptionIntoView(activeRow)
  }

  hideOtherOpenMenus() {
    if (!this.application?.getControllerForElementAndIdentifier) {
      return
    }

    document.querySelectorAll("[data-controller~='railsui-dropdown-menu']").forEach((element) => {
      if (element === this.element) {
        return
      }

      const controller = this.application.getControllerForElementAndIdentifier(
        element,
        "railsui-dropdown-menu"
      )

      if (controller?.isOpen()) {
        controller.hide()
      }
    })
  }

  hide() {
    this.menuTarget.classList.add("hidden")
    this.triggerTarget.setAttribute("aria-expanded", "false")
    this.menuTarget.setAttribute("aria-hidden", "true")
    this.triggerTarget.removeAttribute("aria-activedescendant")

    if (this.hasIconTarget) {
      this.iconTarget.classList.remove("is-open")
    }

    if (this.hasSearchTarget && this.searchTarget.value.length > 0) {
      this.searchTarget.value = ""
      this.filter()
    }

    this.resetCreateForm()
  }

  onTriggerKeydown(event) {
    if (!TRIGGER_KEYS.has(event.key)) {
      return
    }

    event.preventDefault()
    event.stopPropagation()

    const wasOpen = this.isOpen()

    if (wasOpen && ACTIVATION_KEYS.has(event.key)) {
      this.hide()
      return
    }

    if (!wasOpen) {
      this.show()
    }

    this.focusFirstInteractive()
  }

  onMenuKeydown(event) {
    if (!this.isOpen()) {
      return
    }

    if (event.key === "Escape") {
      event.preventDefault()
      this.hide()
      this.triggerTarget.focus()
      return
    }

    if (this.hasSearchTarget && event.target === this.searchTarget) {
      if (event.key === "ArrowDown") {
        event.preventDefault()
        this.focusFirstMenuItem()
      }

      return
    }

    if (event.target.matches("input[type='checkbox']") && event.key === "Enter") {
      event.preventDefault()
      event.target.checked = !event.target.checked
      event.target.dispatchEvent(new Event("change", { bubbles: true }))
      return
    }

    const option = event.target.closest?.("[role='option']")
    if (event.key === "Enter" && option) {
      event.preventDefault()
      option.click()
      return
    }

    if (!["ArrowDown", "ArrowUp"].includes(event.key)) {
      return
    }

    event.preventDefault()
    this.moveFocus(event.target, event.key === "ArrowDown" ? 1 : -1)
  }

  select(event) {
    const row = event.currentTarget

    if (this.modeValue !== "single") {
      return
    }

    this.rowTargets.forEach((menuRow) => menuRow.classList.remove("is-selected"))
    row.classList.add("is-selected")

    this.syncOptionAria()
    this.syncActiveDescendant(row)
    this.updateSummary()
    this.updateHiddenInput({ emitChange: true })
    this.hide()
    this.triggerTarget.focus()
  }

  selectValue(value, { emitChange = false } = {}) {
    this.rowTargets.forEach((row) => row.classList.toggle("is-selected", row.dataset.value === String(value)))
    this.syncOptionAria()
    this.updateSummary()
    this.updateHiddenInput({ emitChange })
  }

  toggleCheckbox(event) {
    const checkbox = event.target

    if (this.hierarchicalValue) {
      this.syncHierarchyFrom(checkbox)
    }

    this.updateSummary()
    this.updateHiddenInput()
    this.syncOptionAria()
    this.submitFilterForm()
  }

  filter(event) {
    const query = event?.target?.value ?? (this.hasSearchTarget ? this.searchTarget.value : "")
    this.applyFilter(query)
    this.syncSearchClearVisibility()
  }

  clearSearch(event) {
    event.preventDefault()
    event.stopPropagation()

    if (!this.hasSearchTarget) {
      return
    }

    this.searchTarget.value = ""
    this.filter()
    this.searchTarget.focus()
  }

  createOption(event) {
    event.preventDefault()

    if (this.hasCreateEditorTarget && this.hasCreateInputTarget) {
      this.openCreateForm()
      return
    }

    const label = this.defaultCreateLabel()

    if (!label) {
      return
    }

    this.commitCreate(label)
  }

  submitCreate(event) {
    event.preventDefault()

    const label = this.hasCreateInputTarget ? this.createInputTarget.value.trim() : this.defaultCreateLabel()

    if (!label) {
      if (this.hasCreateInputTarget) {
        this.createInputTarget.focus()
      }

      return
    }

    this.resetCreateForm()
    this.commitCreate(label)
  }

  closeCreateForm(event) {
    if (event) {
      event.preventDefault()
    }

    this.resetCreateForm()

    if (!this.isOpen()) {
      return
    }

    if (this.hasCreateTarget) {
      this.createTarget.focus()
      return
    }

    if (this.hasSearchTarget) {
      this.searchTarget.focus()
    }
  }

  handleCreateInputKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.submitCreate(event)
      return
    }

    if (event.key === "Escape") {
      event.preventDefault()
      this.closeCreateForm(event)
    }
  }

  handleDocumentClick(event) {
    if (this.element.contains(event.target)) {
      return
    }

    this.hide()
  }

  handleDocumentKeydown(event) {
    if (event.key !== "Escape" || !this.isOpen()) {
      return
    }

    this.hide()
    this.triggerTarget.focus()
  }

  normalizeRows() {
    this.rowTargets.forEach((row, index) => {
      if (!row.dataset.id) {
        row.dataset.id = row.dataset.value || `row-${index + 1}`
      }

      if (!row.hasAttribute("data-value")) {
        row.dataset.value = row.dataset.id
      }

      if (!row.id) {
        row.id = row.dataset.id
      }

      if (this.modeValue === "single" && !row.hasAttribute("role")) {
        row.setAttribute("role", "option")
      }
    })
  }

  syncAll() {
    if (this.hierarchicalValue) {
      this.syncAllParents()
    }

    this.updateSummary()
    this.updateHiddenInput()
  }

  syncHierarchyFrom(checkbox) {
    const row = checkbox.closest("[data-railsui-dropdown-menu-target~='row']")

    if (!row) {
      return
    }

    const rowId = this.rowId(row)
    const childRows = this.descendantsFor(rowId)

    if (childRows.length > 0) {
      childRows.forEach((childRow) => {
        const childCheckbox = this.checkboxFor(childRow)

        if (!childCheckbox) {
          return
        }

        childCheckbox.checked = checkbox.checked
        childCheckbox.indeterminate = false
      })
    }

    this.syncAncestors(row)
  }

  syncAllParents() {
    this.parentRows().reverse().forEach((row) => this.syncParent(row))
  }

  syncAncestors(row) {
    let currentParentId = row.dataset.parentId

    while (currentParentId) {
      const parentRow = this.rowById(currentParentId)

      if (!parentRow) {
        break
      }

      this.syncParent(parentRow)
      currentParentId = parentRow.dataset.parentId
    }
  }

  syncParent(parentRow) {
    const children = this.childrenFor(this.rowId(parentRow))
    const parentCheckbox = this.checkboxFor(parentRow)

    if (!parentCheckbox || children.length === 0) {
      return
    }

    const childCheckboxes = children
      .map((row) => this.checkboxFor(row))
      .filter(Boolean)

    const checkedCount = childCheckboxes.filter((checkbox) => checkbox.checked).length

    if (checkedCount === 0) {
      parentCheckbox.checked = false
      parentCheckbox.indeterminate = false
      return
    }

    if (checkedCount === childCheckboxes.length) {
      parentCheckbox.checked = true
      parentCheckbox.indeterminate = false
      return
    }

    parentCheckbox.checked = false
    parentCheckbox.indeterminate = true
  }

  updateSummary() {
    if (this.modeValue === "single") {
      const selectedRow = this.rowTargets.find((row) => row.classList.contains("is-selected"))
      const hasSelection = Boolean(selectedRow)
      const label = hasSelection ? this.rowLabel(selectedRow) : this.placeholderValue

      this.summaryTarget.textContent = label
      this.summaryTarget.classList.toggle("is-selected", hasSelection)
      this.triggerTarget.classList.toggle("has-selection", hasSelection)
      this.triggerTarget.classList.toggle("dropdown-field-control-filled", hasSelection)
      this.syncClearAffordance(hasSelection)
      return
    }

    const selectedValues = this.selectedValues()
    const count = selectedValues.length
    const hasSelection = count > 0
    const labels = selectedValues.map((value) => this.labelForValue(value) || value)
    const label =
      count === 0
        ? this.placeholderValue
        : this.hasSelectedListTarget
          ? labels.join(", ")
          : count === 1
            ? labels[0]
            : `${count} selected`

    if (this.hasSummaryTarget) {
      this.summaryTarget.textContent = label
      this.summaryTarget.classList.toggle("is-selected", hasSelection)
    }

    this.updateFilterSelection()
    this.updateSelectedList()
    this.triggerTarget.classList.toggle("has-selection", hasSelection)
    this.triggerTarget.classList.toggle("filter-dropdown-control-filled", hasSelection)
    this.triggerTarget.classList.toggle("dropdown-field-control-filled", hasSelection)
    this.syncClearAffordance(hasSelection)
  }

  syncClearAffordance(hasSelection) {
    const clearButton = this.element.querySelector("[data-dropdown-clear]")
    if (!clearButton) return

    clearButton.hidden = !hasSelection
    clearButton.setAttribute("aria-hidden", String(!hasSelection))
  }

  removeFilterSelection(event) {
    const removeControl = event.target.closest(".filter-dropdown-token-remove")

    if (!removeControl || !this.filterSelectionTarget.contains(removeControl)) {
      return
    }

    event.preventDefault()
    event.stopPropagation()

    const token = removeControl.closest(".filter-dropdown-token")
    this.uncheckValue(token?.dataset.value)
    this.triggerTarget.focus()
  }

  removeSelectedValue(event) {
    const control = event.target.closest(".dropdown-selected-list-remove")

    if (!control || !this.hasSelectedListTarget || !this.selectedListTarget.contains(control)) {
      return
    }

    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation?.()

    const listItem = control.closest(".dropdown-selected-list-item")
    const value = control.dataset.value || listItem?.dataset.value
    const nextValue =
      listItem?.nextElementSibling?.dataset.value || listItem?.previousElementSibling?.dataset.value

    this.uncheckValue(value)

    const nextButton = nextValue
      ? Array.from(this.selectedListTarget.querySelectorAll(".dropdown-selected-list-item"))
          .find((item) => item.dataset.value === nextValue)
          ?.querySelector(".dropdown-selected-list-remove")
      : null

    if (nextButton) {
      nextButton.focus()
      return
    }

    this.triggerTarget.focus()
  }

  checkValue(value, { emitChange = true } = {}) {
    if (!value) {
      return
    }

    const normalizedValue = String(value)
    const row = this.rowTargets.find(
      (candidate) => candidate.dataset.value === normalizedValue,
    )
    const checkbox = row ? this.checkboxFor(row) : null

    if (!checkbox) {
      this.ensureHiddenInputIncludes(normalizedValue)
      this.ensureSelectedListItemForValue(normalizedValue)
      this.updateSummary()
      return
    }

    if (checkbox.checked) {
      return
    }

    checkbox.checked = true
    checkbox.indeterminate = false

    if (emitChange) {
      checkbox.dispatchEvent(new Event("change", { bubbles: true }))
      return
    }

    this.updateSummary()
    this.updateHiddenInput({ emitChange: false })
    this.syncOptionAria()
  }

  uncheckValue(value, { emitChange = true } = {}) {
    if (!value) {
      return
    }

    const normalizedValue = String(value)
    const rows = this.rowTargets.filter(
      (candidate) => candidate.dataset.value === normalizedValue,
    )

    if (rows.length === 0) {
      this.removeHiddenInputValue(normalizedValue)
      this.removeSelectedListItemForValue(normalizedValue)
      this.updateSummary()
      return
    }

    let changed = false

    rows.forEach((row) => {
      const checkbox = this.checkboxFor(row)
      if (!checkbox || (!checkbox.checked && !checkbox.indeterminate)) {
        return
      }

      checkbox.checked = false
      checkbox.indeterminate = false
      changed = true
    })

    if (!changed) {
      return
    }

    if (emitChange) {
      const checkbox = this.checkboxFor(rows[0])
      checkbox?.dispatchEvent(new Event("change", { bubbles: true }))
      return
    }

    this.updateSummary()
    this.updateHiddenInput({ emitChange: false })
    this.syncOptionAria()
  }

  hiddenInputValues() {
    if (!this.hasHiddenInputTarget) {
      return []
    }

    return (this.hiddenInputTarget.value || "")
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean)
  }

  ensureHiddenInputIncludes(value) {
    if (!this.hasHiddenInputTarget) {
      return
    }

    const normalizedValue = String(value)
    const values = this.hiddenInputValues()

    if (values.includes(normalizedValue)) {
      return
    }

    this.hiddenInputTarget.value = [...values, normalizedValue].join(",")
  }

  removeHiddenInputValue(value) {
    if (!this.hasHiddenInputTarget) {
      return
    }

    const normalizedValue = String(value)
    const next = this.hiddenInputValues().filter(
      (entry) => entry !== normalizedValue,
    )
    this.hiddenInputTarget.value = next.join(",")
  }

  ensureSelectedListItemForValue(value, label = null) {
    if (!this.hasSelectedListTarget) {
      return
    }

    const normalizedValue = String(value)
    const existingItem = Array.from(
      this.selectedListTarget.querySelectorAll(".dropdown-selected-list-item"),
    ).find((item) => item.dataset.value === normalizedValue)

    if (existingItem) {
      return
    }

    const resolvedLabel = label || this.labelForValue(normalizedValue) || normalizedValue

    const item = this.buildSelectedListItemFromLabel(normalizedValue, resolvedLabel)
    if (!item) {
      return
    }

    this.selectedListTarget.append(item)
    this.selectedListTarget.hidden = false
  }

  removeSelectedListItemForValue(value) {
    if (!this.hasSelectedListTarget) {
      return
    }

    const normalizedValue = String(value)
    const item = Array.from(
      this.selectedListTarget.querySelectorAll(".dropdown-selected-list-item"),
    ).find((candidate) => candidate.dataset.value === normalizedValue)

    item?.remove()

    if (this.selectedListTarget.children.length === 0) {
      this.selectedListTarget.hidden = true
    }
  }

  labelForValue(value) {
    const row = this.rowTargets.find((candidate) => candidate.dataset.value === value)
    if (row) {
      return this.rowLabel(row)
    }

    const existingLabel = Array.from(
      this.selectedListTarget?.querySelectorAll(".dropdown-selected-list-item") || [],
    )
      .find((item) => item.dataset.value === value)
      ?.querySelector(".dropdown-selected-list-label")
      ?.textContent?.trim()

    return existingLabel || null
  }

  buildSelectedListItemFromLabel(value, label) {
    if (this.hasSelectedListItemTemplateTarget) {
      const item =
        this.selectedListItemTemplateTarget.content.firstElementChild.cloneNode(true)
      item.dataset.value = value

      const labelNode = item.querySelector(".dropdown-selected-list-label")
      if (labelNode) {
        labelNode.textContent = label
      }

      const removeButton = item.querySelector(".dropdown-selected-list-remove")
      if (removeButton) {
        removeButton.dataset.value = value
        removeButton.setAttribute("aria-label", `Remove ${label}`)
      }

      return item
    }

    const templateItem = this.selectedListTarget?.querySelector(
      ".dropdown-selected-list-item",
    )
    if (!templateItem) {
      return null
    }

    const item = templateItem.cloneNode(true)
    item.dataset.value = value

    const labelNode = item.querySelector(".dropdown-selected-list-label")
    if (labelNode) {
      labelNode.textContent = label
    }

    const removeButton = item.querySelector(".dropdown-selected-list-remove")
    if (removeButton) {
      removeButton.dataset.value = value
      removeButton.setAttribute("aria-label", `Remove ${label}`)
    }

    return item
  }

  clearSelections({ submit = false, emitChange = false } = {}) {
    if (this.modeValue === "single") {
      this.rowTargets.forEach((row) => row.classList.remove("is-selected"))
    } else {
      this.checkboxTargets.forEach((checkbox) => {
        checkbox.checked = false
        checkbox.indeterminate = false
      })

      if (this.hasHiddenInputTarget) {
        this.hiddenInputTarget.value = ""
      }
    }

    this.updateSummary()
    this.updateHiddenInput({ emitChange })
    this.syncOptionAria()
    this.hide()

    if (submit) {
      this.submitFilterForm()
    }
  }

  clearField(event) {
    event?.preventDefault()
    event?.stopPropagation()
    event?.stopImmediatePropagation?.()

    if (!this.hasSelection() && !(this.hasHiddenInputTarget && this.hiddenInputTarget.value)) {
      return
    }

    this.clearSelections({ submit: false, emitChange: true })
    this.triggerTarget?.focus()
  }

  hasSelection() {
    if (this.modeValue === "single") {
      return this.rowTargets.some((row) => row.classList.contains("is-selected"))
    }

    return this.selectedValues().length > 0
  }

  setLoading(message) {
    this.setMessage(message)
  }

  setMessage(message) {
    if (this.modeValue !== "single") {
      return
    }

    this.clearOptionRows()
    this.clearSingleSelection()
    this.setShellEnabled(false)

    if (this.hasSummaryTarget) {
      this.summaryTarget.textContent = message
      this.summaryTarget.classList.remove("is-selected")
    }

    this.triggerTarget?.classList.remove("has-selection", "dropdown-field-control-filled")
    this.syncClearAffordance(false)
    this.updateHiddenInput({ emitChange: false })
    this.hide()
  }

  setOptions(items = [], { emitChange = false } = {}) {
    if (this.modeValue !== "single") {
      return
    }

    this.clearOptionRows()
    this.clearSingleSelection()

    items.forEach((item) => {
      const label = item.label ?? item.name ?? ""
      const value = String(item.value ?? item.id ?? "")
      if (!value) return

      this.appendMenuRow(this.buildSingleOption(label, value))
    })

    this.setShellEnabled(items.length > 0)
    this.normalizeRows()
    this.updateSummary()
    this.updateHiddenInput({ emitChange })
    this.syncOptionAria()
    this.hide()
  }

  clearOptionRows() {
    if (!this.hasMenuTarget) {
      return
    }

    this.menuTarget
      .querySelectorAll("[data-railsui-dropdown-menu-target='row'], [data-dropdown-menu-message='true']")
      .forEach((row) => row.remove())
  }

  clearSingleSelection() {
    this.rowTargets.forEach((row) => row.classList.remove("is-selected"))
  }

  setShellEnabled(enabled) {
    if (!this.hasTriggerTarget) {
      return
    }

    this.triggerTarget.disabled = !enabled
    this.triggerTarget.classList.toggle("dropdown-field-control-disabled", !enabled)
  }

  updateHiddenInput({ emitChange } = {}) {
    if (!this.hasHiddenInputTarget) {
      return
    }

    const previousValue = this.hiddenInputTarget.value

    if (this.modeValue === "single") {
      const selectedRow = this.rowTargets.find((row) => row.classList.contains("is-selected"))
      this.hiddenInputTarget.value = selectedRow ? selectedRow.dataset.value : ""
    } else {
      this.hiddenInputTarget.value = this.selectedValues().join(",")
    }

    this.syncPanels()

    const valueChanged = this.hiddenInputTarget.value !== previousValue
    const shouldEmit = emitChange === undefined ? this.emitHiddenChangeValue : emitChange

    if (valueChanged && shouldEmit) {
      this.hiddenInputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  syncPanels() {
    if (!this.hasPanelSelectorValue || !this.hasHiddenInputTarget) {
      return
    }

    const selected = this.hiddenInputTarget.value
    const panels = Array.from(document.querySelectorAll(this.panelSelectorValue))

    panels.forEach((panel, index) => {
      const value = panel.dataset.railsuiDropdownMenuValue || String(index)
      const isSelected = value === selected

      panel.hidden = !isSelected
      panel.tabIndex = isSelected ? 0 : -1
    })
  }

  applyFilter(rawQuery) {
    const query = String(rawQuery || "").trim().toLowerCase()

    if (query.length === 0) {
      this.rowTargets.forEach((row) => row.classList.remove("hidden"))
    } else if (!this.hierarchicalValue) {
      this.rowTargets.forEach((row) => {
        const matches = this.rowSearchText(row).includes(query)
        row.classList.toggle("hidden", !matches)
      })
    } else {
      const visibleIds = new Set()

      this.rowTargets.forEach((row) => {
        if (!this.rowSearchText(row).includes(query)) {
          return
        }

        visibleIds.add(this.rowId(row))
        this.addAncestorIds(row, visibleIds)
        this.addDescendantIds(this.rowId(row), visibleIds)
      })

      this.rowTargets.forEach((row) => {
        row.classList.toggle("hidden", !visibleIds.has(this.rowId(row)))
      })
    }

    this.syncActiveDescendant(this.selectedOrFirstVisibleRow())
  }

  addAncestorIds(row, visibleIds) {
    let currentParentId = row.dataset.parentId

    while (currentParentId) {
      visibleIds.add(currentParentId)
      const parentRow = this.rowById(currentParentId)
      currentParentId = parentRow?.dataset.parentId
    }
  }

  addDescendantIds(parentId, visibleIds) {
    this.childrenFor(parentId).forEach((row) => {
      visibleIds.add(this.rowId(row))
      this.addDescendantIds(this.rowId(row), visibleIds)
    })
  }

  rowValueSet() {
    return new Set(this.rowTargets.map((row) => row.dataset.value))
  }

  phantomValues() {
    return this.hiddenInputValues().filter((value) => !this.rowValueSet().has(value))
  }

  selectedValues() {
    const seen = new Set()
    const values = []

    this.checkedRows().forEach((row) => {
      const value = row.dataset.value
      if (!value || seen.has(value)) {
        return
      }

      seen.add(value)
      values.push(value)
    })

    this.phantomValues().forEach((value) => {
      if (seen.has(value)) {
        return
      }

      seen.add(value)
      values.push(value)
    })

    return values
  }

  checkedRows() {
    return this.rowTargets.filter((row) => {
      if (this.hierarchicalValue && this.childrenFor(this.rowId(row)).length > 0) {
        return false
      }

      const checkbox = this.checkboxFor(row)
      return checkbox ? checkbox.checked : false
    })
  }

  parentRows() {
    return this.rowTargets.filter((row) => this.childrenFor(this.rowId(row)).length > 0)
  }

  childrenFor(parentId) {
    return this.rowTargets.filter((row) => row.dataset.parentId === parentId)
  }

  descendantsFor(parentId) {
    const descendants = []

    this.childrenFor(parentId).forEach((row) => {
      descendants.push(row)
      descendants.push(...this.descendantsFor(this.rowId(row)))
    })

    return descendants
  }

  rowById(id) {
    return this.rowTargets.find((row) => this.rowId(row) === id)
  }

  rowId(row) {
    return row.dataset.id
  }

  rowLabel(row) {
    if (row.dataset.label) {
      return row.dataset.label
    }

    const label = row.querySelector(".dropdown-menu-checkbox-label, .dropdown-menu-row-label-text")

    if (label) {
      return label.textContent.trim()
    }

    return row.textContent.trim()
  }

  rowSearchText(row) {
    return this.rowLabel(row).replace(/\s+/g, " ").trim().toLowerCase()
  }

  checkboxFor(row) {
    return row.querySelector("input[type='checkbox']")
  }

  isOpen() {
    return !this.menuTarget.classList.contains("hidden")
  }

  focusFirstInteractive() {
    const [firstElement] = this.interactiveElements()

    if (firstElement) {
      firstElement.focus()
      this.syncActiveDescendant(this.rowForElement(firstElement))
    }
  }

  focusFirstMenuItem() {
    const focusableItems = this.interactiveElements().filter(
      (element) => !this.hasSearchTarget || element !== this.searchTarget
    )
    const [firstElement] = focusableItems

    if (firstElement) {
      firstElement.focus()
      this.syncActiveDescendant(this.rowForElement(firstElement))
    }
  }

  interactiveElements() {
    return Array.from(
      this.menuTarget.querySelectorAll("input[type='search'], input[type='text'], input[type='checkbox'], button")
    ).filter((element) => !element.disabled && element.offsetParent !== null)
  }

  moveFocus(currentElement, direction) {
    const interactiveElements = this.interactiveElements()
    const currentIndex = interactiveElements.indexOf(currentElement)

    if (currentIndex === -1 || interactiveElements.length === 0) {
      return
    }

    const nextIndex =
      (currentIndex + direction + interactiveElements.length) % interactiveElements.length

    interactiveElements[nextIndex].focus()
    this.syncActiveDescendant(this.rowForElement(interactiveElements[nextIndex]))
  }

  syncFocusedOption(event) {
    this.syncActiveDescendant(this.rowForElement(event.target))
  }

  syncOptionAria() {
    this.rowTargets.forEach((row) => {
      if (this.modeValue !== "single") {
        return
      }

      const selected =
        row.classList.contains("is-selected")

      row.setAttribute("aria-selected", selected.toString())
    })
  }

  syncTokenOverflow() {
    if (!this.hasFilterSelectionTarget) {
      return
    }

    const tokens = Array.from(this.filterSelectionTarget.querySelectorAll(".filter-dropdown-token"))
    tokens.forEach((token) => { token.hidden = false })

    const firstLineTop = tokens[0]?.offsetTop
    tokens.forEach((token) => { token.hidden = token.offsetTop > firstLineTop })
  }

  updateFilterSelection() {
    if (!this.hasFilterSelectionTarget) {
      return
    }

    const selectedValues = this.selectedValues()

    this.filterSelectionTarget.replaceChildren()

    if (selectedValues.length === 0) {
      const placeholder = document.createElement("span")
      placeholder.className = "filter-dropdown-placeholder"
      placeholder.dataset.placeholder = "true"
      placeholder.textContent = this.placeholderValue
      this.filterSelectionTarget.append(placeholder)
    } else {
      selectedValues.forEach((value) => {
        const row = this.rowTargets.find((candidate) => candidate.dataset.value === value)

        if (row) {
          this.filterSelectionTarget.append(this.buildFilterToken(row))
          return
        }

        this.filterSelectionTarget.append(this.buildFilterTokenForValue(value))
      })
    }

    this.syncTokenOverflow()

    if (this.hasFilterCountTarget) {
      this.filterCountTarget.textContent = selectedValues.length
      this.filterCountTarget.setAttribute(
        "aria-label",
        `${selectedValues.length} option${selectedValues.length === 1 ? "" : "s"} selected`
      )
    }

    if (this.hasFilterCountWrapperTarget) {
      const tooltip = selectedValues.map((value) => this.labelForValue(value) || value).join(", ")

      this.filterCountWrapperTarget.classList.toggle("hidden", selectedValues.length === 0)
      this.filterCountWrapperTarget.dataset.railsuiTooltipContentValue = tooltip

      if (tooltip.length > 0) {
        this.filterCountWrapperTarget.setAttribute("aria-label", tooltip)
      } else {
        this.filterCountWrapperTarget.removeAttribute("aria-label")
      }

      if (this.filterCountWrapperTarget._tippy) {
        this.filterCountWrapperTarget._tippy.setContent(tooltip)
      }
    }
  }

  updateSelectedList() {
    if (!this.hasSelectedListTarget) {
      return
    }

    const selectedValues = this.selectedValues()
    this.selectedListTarget.replaceChildren()

    if (this.hasSelectedStackTarget) {
      this.selectedStackTarget.hidden = selectedValues.length === 0
    } else {
      this.selectedListTarget.hidden = selectedValues.length === 0
    }

    if (this.hasSelectedTotalCountTarget) {
      this.selectedTotalCountTarget.textContent = String(selectedValues.length)
    }

    selectedValues.forEach((value) => {
      const row = this.rowTargets.find((candidate) => candidate.dataset.value === value)
      const item = row
        ? this.buildSelectedListItem(row)
        : this.buildSelectedListItemFromLabel(value, this.labelForValue(value) || value)

      if (item) {
        this.selectedListTarget.append(item)
      }
    })
  }

  uniqueCheckedRows(selectedRows) {
    const seen = new Set()
    const uniqueRows = []

    selectedRows.forEach((row) => {
      const value = row.dataset.value
      if (!value || seen.has(value)) {
        return
      }

      seen.add(value)
      uniqueRows.push(row)
    })

    return uniqueRows
  }

  buildSelectedListItem(row) {
    const value = row.dataset.value || ""
    const label = this.rowLabel(row)

    if (this.hasSelectedListItemTemplateTarget) {
      const item = this.selectedListItemTemplateTarget.content.firstElementChild.cloneNode(true)
      item.dataset.value = value

      const labelNode = item.querySelector(".dropdown-selected-list-label")
      if (labelNode) {
        labelNode.textContent = label
      }

      const removeButton = item.querySelector(".dropdown-selected-list-remove")
      if (removeButton) {
        removeButton.dataset.value = value
        removeButton.setAttribute("aria-label", `Remove ${label}`)
      }

      return item
    }

    const item = document.createElement("li")
    item.className = "dropdown-selected-list-item"
    item.dataset.value = value

    const labelNode = document.createElement("span")
    labelNode.className = "dropdown-selected-list-label"
    labelNode.textContent = label

    const removeButton = document.createElement("button")
    removeButton.type = "button"
    removeButton.className = "dropdown-selected-list-remove"
    removeButton.dataset.action = "click->railsui-dropdown-menu#removeSelectedValue"
    removeButton.dataset.value = value
    removeButton.setAttribute("aria-label", `Remove ${label}`)

    item.append(labelNode, removeButton)
    return item
  }

  submitFilterForm() {
    if (!this.submitOnChangeValue) {
      return
    }

    const form = this.element.matches("form") ? this.element : this.element.closest("form")

    if (!form) {
      return
    }

    if (typeof form.requestSubmit === "function") {
      form.requestSubmit()
      return
    }

    form.submit()
  }

  buildFilterToken(row) {
    return this.buildFilterTokenForValue(row.dataset.value || "", this.rowLabel(row))
  }

  buildFilterTokenForValue(value, labelText) {
    const token = document.createElement("span")
    token.className = "filter-dropdown-token"
    token.dataset.value = value
    token.dataset.label = labelText

    const label = document.createElement("span")
    label.className = "filter-dropdown-token-label"
    label.textContent = labelText

    if (this.modeValue === "multiple") {
      const icon = document.createElement("button")
      icon.className = "filter-dropdown-token-remove"
      icon.type = "button"
      icon.setAttribute("aria-label", `Remove ${labelText}`)
      icon.dataset.action = "click->railsui-dropdown-menu#removeFilterSelection"
      icon.innerHTML = `<svg class="filter-dropdown-token-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" focusable="false"><path fill-rule="evenodd" d="M4.72 4.72a.75.75 0 0 1 1.06 0L10 8.94l4.22-4.22a.75.75 0 1 1 1.06 1.06L11.06 10l4.22 4.22a.75.75 0 1 1-1.06 1.06L10 11.06l-4.22 4.22a.75.75 0 0 1-1.06-1.06L8.94 10 4.72 5.78a.75.75 0 0 1 0-1.06Z" clip-rule="evenodd" /></svg>`

      token.append(label, icon)
      return token
    }

    token.append(label)

    return token
  }

  syncActiveDescendant(row) {
    if (this.modeValue !== "single" || !row || !row.id) {
      return
    }

    this.triggerTarget.setAttribute("aria-activedescendant", row.id)
  }

  selectedOrFirstVisibleRow() {
    return (
      this.rowTargets.find((row) => row.classList.contains("is-selected") && !row.classList.contains("hidden")) ||
      this.rowTargets.find((row) => !row.classList.contains("hidden"))
    )
  }

  scrollSelectedOptionIntoView(row = this.selectedOrFirstVisibleRow()) {
    if (!row?.classList.contains("is-selected")) {
      return
    }

    const container = this.hasMenuBodyTarget ? this.menuBodyTarget : null
    if (container?.contains(row)) {
      const rowTop = row.getBoundingClientRect().top
      const containerTop = container.getBoundingClientRect().top
      container.scrollTop += rowTop - containerTop
      return
    }

    row.scrollIntoView({ block: "start", inline: "nearest" })
  }

  rowForElement(element) {
    return element?.closest("[data-railsui-dropdown-menu-target~='row']")
  }

  nextCreatedLabel() {
    const createdCount = this.rowTargets.filter((row) => row.dataset.created === "true").length
    return `${this.createLabelValue} ${createdCount + 1}`
  }

  defaultCreateLabel() {
    if (this.hasCreateInputTarget && this.createInputTarget.value.trim().length > 0) {
      return this.createInputTarget.value.trim()
    }

    if (this.hasSearchTarget && this.searchTarget.value.trim().length > 0) {
      return this.searchTarget.value.trim()
    }

    return this.nextCreatedLabel()
  }

  openCreateForm() {
    if (!this.hasCreateEditorTarget || !this.hasCreateInputTarget) {
      return
    }

    const seedValue = this.hasSearchTarget ? this.searchTarget.value.trim() : ""

    if (this.hasSearchTarget && this.searchTarget.value.length > 0) {
      this.searchTarget.value = ""
      this.applyFilter("")
    }

    this.createEditorTarget.classList.remove("hidden")

    if (this.hasCreateTarget) {
      this.createTarget.classList.add("hidden")
    }

    this.createInputTarget.value = seedValue

    requestAnimationFrame(() => {
      this.createInputTarget.focus()
      this.createInputTarget.select()
    })
  }

  syncSearchClearVisibility() {
    if (!this.hasSearchClearTarget || !this.hasSearchTarget) {
      return
    }

    const hasValue = this.searchTarget.value.length > 0
    this.searchClearTarget.classList.toggle("hidden", !hasValue)
  }

  resetCreateForm() {
    if (this.hasCreateEditorTarget) {
      this.createEditorTarget.classList.add("hidden")
    }

    if (this.hasCreateTarget) {
      this.createTarget.classList.remove("hidden")
    }

    if (this.hasCreateInputTarget) {
      this.createInputTarget.value = ""
    }
  }

  commitCreate(label) {
    const value = this.uniqueValue(this.slugify(label) || `created_${Date.now()}`)
    const row =
      this.modeValue === "single"
        ? this.buildSingleOption(label, value)
        : this.buildCheckboxOption(label, value)

    this.appendMenuRow(row)

    if (this.hasSearchTarget) {
      this.searchTarget.value = ""
      this.applyFilter("")
    }

    requestAnimationFrame(() => {
      this.normalizeRows()

      if (this.modeValue === "single") {
        row.click()
        return
      }

      const checkbox = row.querySelector("input[type='checkbox']")

      if (checkbox) {
        checkbox.checked = true
        checkbox.dispatchEvent(new Event("change", { bubbles: true }))
        checkbox.focus()
      }
    })
  }

  appendMenuRow(row) {
    if (this.hasMenuBodyTarget) {
      this.menuBodyTarget.append(row)
      return
    }

    this.menuTarget.append(row)
  }

  uniqueValue(baseValue) {
    let candidate = baseValue
    let index = 2

    while (this.rowTargets.some((row) => row.dataset.value === candidate)) {
      candidate = `${baseValue}_${index}`
      index += 1
    }

    return candidate
  }

  buildSingleOption(label, value) {
    const button = document.createElement("button")

    button.type = "button"
    button.className = "dropdown-menu-item dropdown-menu-button"
    button.dataset.action = "click->railsui-dropdown-menu#select"
    button.dataset.railsuiDropdownMenuTarget = "row"
    button.dataset.id = value
    button.dataset.value = value
    button.dataset.label = label
    button.dataset.created = "true"
    button.id = value
    button.role = "option"
    button.setAttribute("aria-selected", "false")
    button.textContent = label

    return button
  }

  buildCheckboxOption(label, value) {
    const checkboxId = `${value}_checkbox`

    const row = document.createElement("div")
    row.className = "dropdown-menu-checkbox-item"
    row.dataset.railsuiDropdownMenuTarget = "row"
    row.dataset.id = value
    row.dataset.value = value
    row.dataset.label = label
    row.dataset.created = "true"
    row.id = value
    row.role = "option"
    row.setAttribute("aria-selected", "false")

    const choiceLabel = document.createElement("label")
    choiceLabel.className = "dropdown-menu-checkbox-choice"
    choiceLabel.htmlFor = checkboxId

    const checkbox = document.createElement("input")
    checkbox.type = "checkbox"
    checkbox.id = checkboxId
    checkbox.value = value
    checkbox.className = "form-input-checkbox dropdown-menu-checkbox-input"
    checkbox.dataset.railsuiDropdownMenuTarget = "checkbox"
    checkbox.dataset.action = "change->railsui-dropdown-menu#toggleCheckbox"

    const text = document.createElement("span")
    text.className = "dropdown-menu-checkbox-label"
    text.textContent = label

    choiceLabel.append(checkbox, text)
    row.append(choiceLabel)

    return row
  }

  slugify(value) {
    return value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
  }
}
